// FE-SCRM-059 — Search fetcher for ActivityComposerModal.
//
// Wraps the FE-SCRM-054 global-search endpoint and adapts it into
// the TargetSearchPicker's TargetSearchResult shape. Two fetchers:
//
//   * ``searchProspectsAndOpportunities`` — combined search for the
//     anchor picker. Opportunity rows carry the parent
//     ``prospect_id`` so the composer can auto-derive both anchors
//     from one selection.
//
//   * ``searchContactsForProspect`` — contact-picker typeahead
//     restricted to a given prospect's sales contacts. Filters
//     client-side over the prospect-scoped contact list endpoint
//     (FE-SCRM-053) rather than the global search, so the picker
//     never surfaces a contact on a different prospect.

import { fetchContactsList } from "../Contacts/api";
import { fetchGlobalSearch } from "../search/api";

import type { TargetKind, TargetSearchResult } from "../components";


export async function searchProspectsAndOpportunities(
  q: string,
  restrictTo: readonly TargetKind[] | undefined,
): Promise<TargetSearchResult[]> {
  if (!q.trim()) return [];
  const res = await fetchGlobalSearch(q, { limit: 5 });
  const out: TargetSearchResult[] = [];

  const allow = restrictTo ?? ["prospect", "opportunity"];

  if (allow.includes("prospect")) {
    for (const row of res.prospects) {
      out.push({
        id: row.id,
        kind: "prospect",
        label: row.label,
        sublabel: row.sublabel ?? undefined,
      });
    }
  }
  if (allow.includes("opportunity")) {
    for (const row of res.opportunities) {
      out.push({
        id: row.id,
        kind: "opportunity",
        label: row.label,
        sublabel: row.sublabel ?? undefined,
        // BE search serializer populates this; older payloads without
        // the field fall back to undefined and the composer keeps the
        // existing prospect anchor untouched.
        parent_prospect_id: row.prospect_id ?? undefined,
      });
    }
  }
  return out;
}


export async function searchContactsForProspect(
  q: string,
  prospect_id: string,
): Promise<TargetSearchResult[]> {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const list = await fetchContactsList({
    q: trimmed,
    prospect_id: [prospect_id],
    include_archived: false,
    page_size: 10,
  });
  return list.rows.map((row) => ({
    id: row.contact_id,
    kind: "contact" as const,
    label: row.display_name,
    sublabel: row.title ?? row.email ?? undefined,
  }));
}
